import {Inject, Injectable, InjectionToken} from "@angular/core";
import {JwtHttpClientService} from "../http/jwt-http-client.service";
import {EntityData} from "../resource/entity/entity.data";
import {finalize, firstValueFrom, map, Observable, share} from "rxjs";
import {ResourceModule} from "../resource/resource.module";
import {HttpResponse} from "@angular/common/http";

@Injectable()
export class ValidationService {
    /**
     * Static URL prefix for all server side calls
     * @type {string}
     */
    public static readonly URL_PREFIX = '/dataserver/validation/';

    /**
     * Provider application key that is used during dependency injection and is used to identify the application in the translation file.
     * @type {InjectionToken}
     */
    public static readonly APPLICATION_KEY: InjectionToken<string> = new InjectionToken<string>('applicationKey');

    private scriptObservable: Observable<string>;
    private validationFunction: Function;
    private validationFactory: ValidationFactory;
    private factoryPromise: Promise<ValidationFactory>;

    /**
     *
     * @param http HTTP Service
     * @param application Application key used for field name lookup
     * @param nameSpace
     */
    constructor(protected http: JwtHttpClientService,
                @Inject(ValidationService.APPLICATION_KEY) protected application: string,
                @Inject(ResourceModule.RESOURCE_NAMESPACE_KEY) protected nameSpace: string) {
    }

    /**
     * Validate the entity data and all of its fields
     * @param entityData
     * @returns {Promise<T>} a promise of a list of validation messages
     */
    public validate(entityData: EntityData<any>): Promise<ValidationMessage[]> {
        return new Promise(resolve => {
            entityData.getValidation(this)
                .then(validation => validation.validate())
                .then(validationMessages => {
                    if (!validationMessages || validationMessages.length == 0) {
                        resolve(null);
                    } else {
                        resolve(validationMessages);
                    }
                })
                .catch(error => console.log("Failed to perform validation for " + this.getEntityName(entityData) + ": " + error));
        });
    }

    /**
     * Validate the entity data (no field validation)
     * @param entityData
     * @returns {Promise<T>} a promise of a list of validation messages
     */
    public validateEntity(entityData: EntityData<any>): Promise<ValidationMessage[]> {
        return new Promise(resolve => {
            entityData.getValidation(this)
                .then(validation => validation.validateEntity())
                .then(validationMessages => {
                    if (!validationMessages || validationMessages.length == 0) {
                        resolve(null);
                    } else {
                        resolve(validationMessages);
                    }
                })
                .catch(error => console.log("Failed to perform validation for " + this.getEntityName(entityData) + ": " + error));
        });
    }

    /**
     * Validate a child entity data and all of its fields
     * @param {EntityData<any>} entityData
     * @param {EntityData<any>} childEntityData
     * @param {string} fieldPath
     * @param {number} rowIndex
     * @returns {Promise<ValidationMessage[]>}
     */
    public validateChild(entityData: EntityData<any>, childEntityData: EntityData<any>, fieldPath: string, rowIndex: number): Promise<ValidationMessage[]> {
        return new Promise(resolve => {
            entityData.getValidation(this)
                .then(validation => validation.validateChild(childEntityData, fieldPath, rowIndex))
                .then(validationMessages => {
                    if (!validationMessages || validationMessages.length == 0) {
                        resolve(null);
                    } else {
                        resolve(validationMessages);
                    }
                })
                .catch(error => console.log("Failed to perform validation for " + this.getEntityName(entityData) + ": " + error));
        });
    }

    /**
     * Returns a Promise for validation of the specified field in the specified entity data.
     * @param entityData
     * @param fieldValue
     * @param fieldPath
     * @param rowIndex
     * @param fieldIdentifier
     * @returns {Promise<T>} a promise of a list of validation messages
     */
    public validateField(entityData: EntityData<any>, fieldValue: any, fieldPath: string, rowIndex: number, fieldIdentifier: any): Promise<{validationMessages: ValidationMessage[]}> {
        return new Promise(resolve => {
            entityData.getValidation(this)
                .then((validation: Validation) => validation.validateField(fieldPath, fieldValue, undefined, rowIndex, fieldIdentifier))
                .then((validationMessages: ValidationMessage[]) => {
                    if (!validationMessages || validationMessages.length == 0) {
                        resolve(null);
                    } else {
                        resolve({validationMessages: validationMessages});
                    }
                })
                .catch(error => console.log("Failed to perform validation for " + this.getEntityName(entityData) + "." + fieldPath + ": " + error));
        });
    }

    public isFieldRequired(entityData: EntityData<any>, fieldPath: string): Promise<boolean> {
        return new Promise(resolve => {
            entityData.getValidation(this)
                .catch(error => console.log("Failed to get field required for " + this.getEntityName(entityData) + "." + fieldPath + ": " + error))
                .then((validation: Validation) => resolve(validation.isFieldRequired(fieldPath)));
        });
    }

    public hasAnyValidation(entityData: EntityData<any>, fieldPath: string): Promise<boolean> {
        return new Promise(resolve => {
            entityData.getValidation(this)
                .catch(error => console.log("Failed to get hasAnyValidation for " + this.getEntityName(entityData) + "." + fieldPath + ": " + error))
                .then((validation: Validation) => resolve(validation.hasAnyValidation(fieldPath)));
        });
    }

    /**
     * Performs server-side validation of a field
     * @param entityData
     * @param fieldPath
     * @param fieldValue
     * @param rowIndex
     * @param validatorName
     * @param fieldIdentifier
     * @returns {Promise<T>} a promise of a list of validation messages
     */
    public validateFieldOnServer(entityData: EntityData<any>, fieldPath: string, rowIndex: number, fieldIdentifier: any, fieldValue: any, validatorName: string): Promise<ValidationMessage[]> {
        if (rowIndex === undefined) {
            rowIndex = null;
        }
        var body = `{
            "identifier": ${entityData.getIdentifier() === undefined ? null : JSON.stringify(entityData.getIdentifier())},
            "rowIndex": ${rowIndex},
            "fieldValue": ${fieldValue === undefined ? null : JSON.stringify(fieldValue)},
            "fieldIdentifier": ${fieldIdentifier === undefined ? null : JSON.stringify(fieldIdentifier)},
            "entityData": ${entityData.toJSONString()}
        }`;
        return firstValueFrom(this.http.post(ValidationService.URL_PREFIX + this.nameSpace + "/" + this.getEntityName(entityData) + "/" + fieldPath + "/" + validatorName, body).pipe(
            map((response: HttpResponse<Object>) => <ValidationMessage[]> (response.body) || []))
        );
    }

    /**
     * Returns an observable for the validation script text.
     * Multiple concurrent calls to this method will return the same observable, so that the script will not be fetched multiple times.
     * @returns {any}
     */
    private getValidationScript(): Observable<string> {
        // Only allow a single concurrent request
        if (this.scriptObservable) {
            return this.scriptObservable;
        }
        return this.scriptObservable = this.http.get(ValidationService.URL_PREFIX, undefined, undefined, "text").pipe(
            map((response: HttpResponse<string>) => response.body),
            finalize(() => this.scriptObservable = undefined),
            share()
        );
    }

    /**
     * Returns a promise of a ValidationFactory.
     * The ValidationFactory is obtained by invoking the function parsed from the validation script fetched from the server.
     * Multiple concurrent calls to this method will return the same promise, so that the script will not be parsed multiple times.
     * Once the promise has been resolved, subsequent calls to this method will return a new promise and re-parse the script.
     * However, the ValidationFactory itself is cached and re-used by createValidation() below, rather than re-calling this method.
     * @returns {Promise<ValidationFactory>}
     */
    private loadValidationFactory(): Promise<ValidationFactory> {
        // Only allow a single concurrent load
        if (this.factoryPromise) {
            return this.factoryPromise;
        }
        return this.factoryPromise = new Promise((resolve, reject) => {
            this.getValidationScript().subscribe(
                (script: string) => {
                    try {
                        this.validationFunction = new Function(script);
                        resolve(this.validationFactory = this.validationFunction());
                        this.factoryPromise = undefined;
                    } catch (error) {
                        reject("Failed to parse validation script: " + error);
                    }
                }
            );
        })
    }

    /**
     * Returns a promise of a Validation for the specified entity data.
     * If the validation factory function has already been loaded, then it will be immediately invoked to create the Validation,
     * and a pre-resolved Promise of that validation will be returned.
     * Otherwise, the returned Promise will be resolved once the validation factory function has been asynchronously loaded and invoked to create the Validation.
     * Each call to this method will invoke the validation factory function, which means executing the entire validation script to build a separate copy of its entities array.
     * For this reason, direct calls to this method should be avoided in favour of EntityData.getValidation() which caches a single validation for the entity data instance.
     * @param entityData
     * @returns {Promise<Validation>}
     */
    public createValidation(entityData: EntityData<any>): Promise<Validation> {
        if (this.validationFactory) {
            return Promise.resolve(this.validationFactory.createValidation(this, this.nameSpace, this.getEntityName(entityData), entityData, entityData.getIdentifier()));
        }
        return new Promise((resolve, reject) => {
            this.loadValidationFactory()
                .then(validationFactory => {
                    return resolve(validationFactory.createValidation(this, this.nameSpace, this.getEntityName(entityData), entityData, entityData.getIdentifier()));
                })
                .catch(error => reject(error));
        })
    }

    private getEntityName(entityData: EntityData<any>): string {
        return entityData.constructor.name.replace(/^_/, "");
    }
}

/**
 * Typed definition of the ValidationFactory class defined by the validation script fetched from the server
 */
export interface ValidationFactory {
    createValidation(validationService: ValidationService, entityNameSpace: string, entityName: string, entityData: EntityData<any>, identifier: any): Validation;
}

/**
 * Typed definition of the Validation class defined by the validation script fetched from the server
 */
export interface Validation {
    validate(): Promise<ValidationMessage[]>;

    validateEntity(): Promise<ValidationMessage[]>;

    validateChild(entityData: EntityData<any>, fieldPath: string, rowIndex?: number): Promise<ValidationMessage[]>;

    validateField(fieldPath: string, fieldValue: any, component?: any, rowIndex?: number, fieldIdentifier?: string, validatorName?: string, validationDefinition?: any): Promise<ValidationMessage[]>;

    isFieldRequired(fieldPath: string): boolean;

    hasAnyValidation(fieldPath: string): boolean;
}

/**
 * Typed definition of the ValidationMessage class defined by the validation script fetched from the server
 */
export interface ValidationMessage {
    readonly fieldPath: string;
    readonly messageKey: string;
    readonly messageParams: any;
}
